import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { logoutReturnTo, nativeLogoutOpts } from "@/native/auth";
import { clearSessionQueryCaches } from "@/queryPersistence";
import { useAuth0 } from "@auth0/auth0-react";
import { Organization } from "@cohesive/api-client";
import React, { useState } from "react";

interface DeleteOrganizationDialogProps {
  organization: Organization | null;
  onConfirmDelete: (organization: Organization) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export const DeleteOrganizationDialog: React.FC<
  DeleteOrganizationDialogProps
> = ({ organization, onConfirmDelete, onCancel, isLoading = false }) => {
  const [confirmationSlug, setConfirmationSlug] = useState("");
  const { logout } = useAuth0();

  const handleConfirm = async () => {
    if (organization && confirmationSlug === organization.slug) {
      await onConfirmDelete(organization);
      setConfirmationSlug("");
      onCancel();
      clearSessionQueryCaches();
      await logout({
        logoutParams: { returnTo: logoutReturnTo() },
        ...nativeLogoutOpts(),
      });
    }
  };

  const handleCancel = () => {
    setConfirmationSlug("");
    onCancel();
  };

  const isSlugMatch = confirmationSlug === organization?.slug;

  return (
    <Dialog
      open={!!organization}
      onOpenChange={(open) => !open && handleCancel()}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Delete Organization</DialogTitle>
          <DialogDescription className="text-left">
            This action cannot be undone. This will permanently delete the
            organization "{organization?.name}" and all associated data.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md bg-destructive/5 border border-destructive/20 p-4">
            <h4 className="text-sm font-medium text-destructive mb-2">
              This action is irreversible
            </h4>
            <p className="text-sm text-muted-foreground">
              All organization data, users, assistants, and settings will be
              permanently deleted.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmation-slug">
              Type{" "}
              <code className="bg-muted px-1 py-0.5 rounded text-sm font-mono">
                {organization?.slug}
              </code>{" "}
              to confirm:
            </Label>
            <Input
              id="confirmation-slug"
              value={confirmationSlug}
              onChange={(e) => setConfirmationSlug(e.target.value)}
              placeholder={organization?.slug}
              autoComplete="off"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!isSlugMatch || isLoading}
            isLoading={isLoading}
          >
            Delete Organization
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteOrganizationDialog;
